
// @ts-check
export const NodeRelationshipType = Object.freeze({
  DependsOn: 'DEPENDS_ON',
  Contains: 'CONTAINS'
});

export const NodeType = Object.freeze({
  Asset: 'ASSET',
  Dashboard: 'DASHBOARD',
  Node: 'NODE'
});

export const UserPermissions = Object.freeze({
  Owner: 'owner',
  Manager: 'manager',
  Member: 'member',
  ManageTenants: 'tenant_manager',
  ManageOrganizations: 'organization_manager',
  ManageUsers: 'user_manager',
  ManageGroups: 'group_manager',
  ManageAssets: 'asset_manager',
  ManageModels: 'model_manager',
  ManageDashboards: 'dashboard_manager',
  ManageTrees: 'tree_manager',
  ManageNotifications: 'notification_manager',
  ManageDevices: 'device_manager',
  ManageAlarms: 'alarm_manager',
  ManageProviders: 'provider_manager',
  ManageProviderClients: 'provider_client_manager',
  ManageTemplates: 'template_manager',
  ManageWorkflows: 'workflow_manager'
});

/**
 * Enum for possible management rights for users and groups
 * @readonly
 * @enum {string}
  */
export const privileges = Object.freeze({
  Any: 'any',
  AssetManagement: 'asset_management',
  DashboardManagement: 'dashboard_management',
  UserManagement: 'user_management',
  TreeManagement: 'tree_management',
  ModelManagement: 'model_management',
  AlarmManagement: 'alarm_management',
  OrganizationManagement: 'organization_management',
  ProviderManagement: 'provider_management',
  ProviderClientManagement: 'provider_client_management',
  NotificationManagement: 'notification_management'
});


/**
 * Defines the type of resources that exists in Beacon Tower to which relationships can be created.
 *
 * @property {string} Asset
 * @property {string} Node
 * @property {string} Alarm
 * @property {string} Dashboard
 * @property {string} Device
 * @property {string} Model
 * @property {string} Tree
 * @property {string} Workflow
 * @readonly
 * @enum {string}
 * @description The type of resource.
 */
export const ResourceType = Object.freeze({
  Asset: 'asset',
  Node: 'node',
  Alarm: 'alarm',
  Dashboard: 'dashboard',
  Device: 'device',
  Model: 'model',
  Tree: 'tree',
  Workflow: 'workflow',
  Provider: 'provider',
  ProviderClient: 'provider_client',
  ProviderTemplate: 'provider_template',
  User: 'user',
  Group: 'group',
  Notification: 'notification'
});

export const ResourceRelationType = Object.freeze({
  Assets: 'assets',
  Dashboards: 'dashboards',
  Alarms: 'alarms',
  Providers: 'providers',
  ProviderClients: 'provider_clients',
  ProviderTemplates: 'provider_templates',
  Models: 'models',
  Nodes: 'nodes',
  Workflows: 'workflows',
  Devices: 'devices',
  Users: 'users',
  Groups: 'groups',
  Trees: 'trees',
  Notifications: 'notifications'
});


export const ResourceRelationLevel = Object.freeze({
  none: 'none',
  member: 'member',
  owner: 'owner',
  manager: 'manager'
});

export const OrgRelationAccess = Object.freeze({
  none: 'none',
  viewer: 'viewer',
  owner: 'owner',
  manager: 'manager'
});


export const RelationshipType = Object.freeze({
  owner: 'owner',
  member: 'member',
  admin: 'admin',
  alarm_admin: 'alarm_admin',
  asset_admin: 'asset_admin',
  dashboard_admin: 'dashboard_admin',
  device_admin: 'device_admin',
  model_admin: 'model_admin',
  tree_admin: 'tree_admin',
  workflow_admin: 'workflow_admin',
  notification_admin: 'notification_admin'
});

/**
 * Relationship is a struct that conatains an UGO type, and id and a relationship type.
 * @typedef {Object} Relationship
 * @property {RelationshipType} type - THe type of the relationship.
 * @property {any} ugoType - The UGO type of the relationship. TODO: Add type
 * @property {string} ugoId - The id of the UGO.
*/

/**
 *
 * @readonly
 * @enum {string}
 */
export const ConnectionState = Object.freeze({
  Connected: 'Connected',
  Disconnected: 'Disconnected',
  Unknown: 'Unknown'
});

export const PlotlineTypes = Object.freeze({
  ReportedProperty: 'REPORTED_PROPERTY',
  DesiredProperty: 'DESIRED_PROPERTY',
  StaticValue: 'STATIC_VALUE'
});

export const LineStyles = Object.freeze({
  Solid: 'Solid',
  Dash: 'Dash',
  ShortDash: 'ShortDash',
  Dot: 'Dot',
  DashDot: 'DashDot',
  ShortDashDot: 'ShortDashDot',
  LongDashDot: 'LongDashDot'
});

/**
 * @typedef {Object} Provider
 * @property {string} id
 * @property {string} displayName
 */
/**
 * @typedef {Object} BindingData
 * @property {Array<string>} [autobindRemoteInstanceIds]
 */

// TODO: Update types in AssetType
/**
 * @typedef {Object} AssetType
 * @property {string} id
 * @property {string} displayName
 * @property {string} modelId
 * @property {Provider[]} providers
 * @property {BindingData} bindingData
 * @property {object} [ioTDevice]
 */


// Had to define property likes this because of the '@' sign in the property name
/**
 * @typedef {{
 * '@id': string,
 * '@type': string,
 * 'displayName': string,
 * 'component': string,
 * 'componentName': string,
 * 'modelId': string,
 * 'schema': string,
 * 'writable': boolean,
 * 'reported': number | string,
 * 'desired': number | string
 * }} Property

/**
 * @typedef {Object} OrgSettingsType
 * @property {string} theme
 * @property {string} language
 * @property {string} units
 * @property {string[]} orgFilter
 * @property {boolean} needLicenseAgreement
 */

/**
 * @typedef {Object} OrgType
 * @property {string} id
 * @property {string} displayName
 * @property {string} description
 */
